import type { BlogArticleAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { BlogArticleCategory, BlogArticlePinnedTo } from '@/client/services/blog'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const BlogArticleQ = {
  ...makeQueryModelV2<BlogArticleAttributes>('blog_article')([
    'id',
    'user_id',
    'excerpt',
    'title',
    'slug',
    'meta_description',
    'category',
    'pinned_to',
    'body',
    'image_url',
    'published_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: BlogArticleQ.user_id,
    },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: BlogArticleQ.user_id,
    }),

  isPublished: (): SQLFragment<boolean> => SQL`
        ${BlogArticleQ.published_at} < NOW()
    `,

  where: (
    {
      id,
      userId,
      slug,
      category,
      pinnedTo,
      groupBy = null,
      include = [],
      limit = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      slug?: string | null | SQLFragment<string>
      category?: BlogArticleCategory | null | SQLFragment<BlogArticleCategory>
      pinnedTo?: BlogArticlePinnedTo | null | SQLFragment<BlogArticlePinnedTo>
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      include?: IncludeAssociations
      paranoid?: boolean
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<BlogArticleAttributes> => SQL`
        FROM ${BlogArticleQ}
        ${includeAssociationsToSql(BlogArticleQ, include)}
        WHERE
            ${id != null ? SQL`${BlogArticleQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${BlogArticleQ.user_id} = ${userId} AND` : SQL``}
            ${slug != null ? SQL`${BlogArticleQ.slug} = ${slug} AND` : SQL``}
            ${category != null ? SQL`${BlogArticleQ.category} = ${category} AND` : SQL``}
            ${pinnedTo != null ? SQL`${BlogArticleQ.pinned_to} = ${pinnedTo} AND` : SQL``}
            ${paranoid ? SQL`${BlogArticleQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,
}

export default BlogArticleQ
